import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { CalendarPlus, X } from "lucide-react";
import NavigationStepperCst from "@components/NavigationStepperCst";
import { useWizardState } from "@components/WizardCst/hooks/useWizardState";
import { ValidationResult, WizardStep } from "@components/WizardCst/types";
import { openToast } from "@utils/toastStore";
import MainInfoStep from "./steps/MainInfoStep";
import DeliveryMethodStep from "./steps/DeliveryMethodStep";
import DeliveryDetailsStep from "./steps/DeliveryDetailsStep";
import SiteStep from "./steps/SiteStep";
import RoomStep from "./steps/RoomStep";
import { useGetEnrollmentStatus } from "@hooks/useGetEnrollmentStatus";
import {
  BaseSessionDraft,
  PersonRef,
  PhysicalSessionDraft,
  SessionDraft,
  SessionWizardTriggerPayload,
  VirtualSessionDraft,
  createEmptyPhysicalSessionDraft,
  createEmptyVirtualSessionDraft,
} from "./types";
import {
  computeCreateDisabledFields,
  computeDisabledFields,
  sessionToDraft,
} from "./editMapping";
import { MEETING_SETTINGS } from "./config/meetingSettings";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import { ButtonPrimary } from "src/UI/ButtonPrimary";

interface SessionWizardModalProps {
  trigger: SessionWizardTriggerPayload;
  onClose: () => void;
}

const syncFacultyTutorRoles = (
  oldPeople: PersonRef[],
  newPeople: PersonRef[],
  v: VirtualSessionDraft,
): Pick<VirtualSessionDraft, "presenter" | "cohost"> => {
  const newIds = new Set(newPeople.map((p) => String(p.id)));
  const oldIds = new Set(oldPeople.map((p) => String(p.id)));
  const removedIds = new Set(
    oldPeople.filter((p) => !newIds.has(String(p.id))).map((p) => String(p.id)),
  );
  const added = newPeople.filter((p) => !oldIds.has(String(p.id)));

  const presenter = v.presenter.filter((p) => !removedIds.has(String(p.id)));
  const cohost = v.cohost.filter((p) => !removedIds.has(String(p.id)));

  const presenterIds = new Set(presenter.map((p) => String(p.id)));
  const cohostIds = new Set(cohost.map((p) => String(p.id)));
  const toAdd = added.filter(
    (p) => !presenterIds.has(String(p.id)) && !cohostIds.has(String(p.id)),
  );

  return {
    presenter: [...presenter, ...toAdd],
    cohost: [...cohost, ...toAdd],
  };
};

const SessionWizardModal: React.FC<SessionWizardModalProps> = ({
  trigger,
  onClose,
}) => {
  const { t } = useTranslation("session-wizard");
  const theme = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isVirtual = trigger.kind === "virtual";
  const isEdit = !!trigger.editSession;

  const { enrollmentStatus } = useGetEnrollmentStatus(
    trigger.editionId != null ? String(trigger.editionId) : undefined,
    isEdit,
  );

  const steps = useMemo<WizardStep<SessionDraft>[]>(() => {
    const validateMain = (d: SessionDraft): ValidationResult => {
      const errors: Record<string, string> = {};
      if (!d.start_date) errors.start_date = String(t("errors.required"));
      if (!d.end_date) errors.end_date = String(t("errors.required"));
      if (
        d.start_date &&
        d.end_date &&
        !dayjs(d.end_date).isAfter(dayjs(d.start_date))
      ) {
        errors.end_date = String(t("errors.end-before-start"));
      }
      return { valid: Object.keys(errors).length === 0, errors };
    };

    const mainStep: WizardStep<SessionDraft> = {
      key: "main",
      label: String(t("steps.main")),
      Component: () => null,
      validate: validateMain,
    };

    if (isVirtual) {
      return [
        mainStep,
        {
          key: "method",
          label: String(t("steps.method")),
          Component: () => null,
          validate: (d): ValidationResult => {
            const errors: Record<string, string> = {};
            if (!(d as VirtualSessionDraft).tool) {
              errors.tool = String(t("errors.tool-required"));
            }
            return { valid: Object.keys(errors).length === 0, errors };
          },
        },
        {
          key: "details",
          label: String(t("steps.details")),
          Component: () => null,
          validate: (d): ValidationResult => {
            const v = d as VirtualSessionDraft;
            const errors: Record<string, string> = {};
            const fields = v.tool ? MEETING_SETTINGS[v.tool] : [];
            fields.forEach((f) => {
              if (f.required && !v[f.key]) {
                errors[f.key] = String(t("errors.required"));
              }
            });
            return { valid: Object.keys(errors).length === 0, errors };
          },
        },
      ];
    }

    return [
      mainStep,
      {
        key: "site",
        label: String(t("steps.site")),
        Component: () => null,
        validate: (d): ValidationResult => {
          const errors: Record<string, string> = {};
          if (!(d as PhysicalSessionDraft).site) {
            errors.site = String(t("errors.site-required"));
          }
          return { valid: Object.keys(errors).length === 0, errors };
        },
      },
      {
        key: "room",
        label: String(t("steps.room")),
        Component: () => null,
        validate: (d): ValidationResult => {
          const errors: Record<string, string> = {};
          if ((d as PhysicalSessionDraft).rooms.length === 0) {
            errors.rooms = String(t("errors.room-required"));
          }
          return { valid: Object.keys(errors).length === 0, errors };
        },
      },
    ];
  }, [t, isVirtual]);

  const initialData = useMemo<SessionDraft>(
    () =>
      trigger.editSession
        ? sessionToDraft(trigger.editSession, trigger.kind)
        : isVirtual
          ? createEmptyVirtualSessionDraft()
          : createEmptyPhysicalSessionDraft(),
    [isVirtual, trigger.editSession, trigger.kind],
  );

  const confirmedDisabledReason =
    isEdit && enrollmentStatus && !enrollmentStatus.allEnrollmentsCompleted
      ? String(t("enrollment-blocked"))
      : undefined;

  const {
    activeStep,
    data,
    setData,
    errors,
    tryNext,
    goBack,
    goToStep,
    isFirstStep,
    isLastStep,
  } = useWizardState<SessionDraft>({ steps, initialData });

  const draftTool = (data as VirtualSessionDraft).tool;
  const disabledFields = useMemo<Set<string>>(
    () =>
      trigger.editSession
        ? computeDisabledFields(
            trigger.editSession,
            trigger.kind,
            trigger.publishDate,
            enrollmentStatus?.allEnrollmentsCompleted,
          )
        : computeCreateDisabledFields(
            trigger.kind,
            draftTool,
            trigger.publishDate,
          ),
    [
      trigger.editSession,
      trigger.kind,
      trigger.publishDate,
      enrollmentStatus,
      draftTool,
    ],
  );

  const handleNext = async () => {
    if (isSubmitting) return;
    if (!isLastStep) {
      tryNext();
      return;
    }
    const result = steps[activeStep]?.validate?.(data);
    if (result && !result.valid) {
      tryNext();
      return;
    }
    setIsSubmitting(true);
    try {
      await trigger.onConfirm(data);
      onClose();
      openToast({
        severity: "success",
        message: String(t("feedback.success")),
      });
    } catch (e: any) {
      openToast({
        severity: "error",
        message: e?.message || String(t("feedback.error")),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const commonProps = {
    errors,
    corporateId: trigger.corporateId,
    disabledFields,
    confirmedDisabledReason,
    publishDate: trigger.publishDate,
  };

  const baseProps = {
    ...commonProps,
    data: data as BaseSessionDraft,
    setData: setData as (patch: Partial<BaseSessionDraft>) => void,
  };

  const virtualProps = {
    ...commonProps,
    data: data as VirtualSessionDraft,
    setData: setData as (patch: Partial<VirtualSessionDraft>) => void,
  };

  const physicalProps = {
    ...commonProps,
    data: data as PhysicalSessionDraft,
    setData: setData as (patch: Partial<PhysicalSessionDraft>) => void,
  };

  const mainSetData = (patch: Partial<BaseSessionDraft>) => {
    if (isVirtual && (patch.faculties || patch.tutors)) {
      const v = data as VirtualSessionDraft;
      const newPeople = (patch.faculties ?? patch.tutors) as PersonRef[];
      const oldPeople = patch.faculties ? v.faculties : v.tutors;
      setData({
        ...patch,
        ...syncFacultyTutorRoles(oldPeople, newPeople, v),
      } as Partial<SessionDraft>);
      return;
    }
    setData(patch as Partial<SessionDraft>);
  };

  const renderStep = () => {
    if (activeStep === 0)
      return <MainInfoStep {...baseProps} setData={mainSetData} />;
    if (isVirtual) {
      if (activeStep === 1) return <DeliveryMethodStep {...virtualProps} />;
      return <DeliveryDetailsStep {...virtualProps} />;
    }
    if (activeStep === 1) return <SiteStep {...physicalProps} />;
    return <RoomStep {...physicalProps} />;
  };

  return (
    <Dialog
      open
      onClose={isSubmitting ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle sx={{ padding: "20px 24px 0" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Box>
            {trigger.contextLabel && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: theme.palette.text.primary,
                }}
              >
                <CalendarPlus size={16} />
                <Typography sx={{ fontSize: "13px" }}>
                  {trigger.contextLabel}
                </Typography>
              </Box>
            )}
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                color: theme.palette.text.primary,
                mt: trigger.contextLabel ? 0.5 : 0,
              }}
            >
              {isEdit ? t("title-edit") : t("title")}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="close"
            disabled={isSubmitting}
            sx={{ color: theme.palette.text.primary }}
          >
            <X size={20} />
          </IconButton>
        </Box>

        <Box sx={{ mt: 2.5 }}>
          <NavigationStepperCst
            steps={steps.map((s) => ({ key: s.key, label: s.label }))}
            activeStep={activeStep}
            onStepChange={goToStep}
            allowBackNavigation
          />
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          mt: 3,
          padding: "28px 24px 24px",
          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
          "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
        }}
      >
        {renderStep()}
      </DialogContent>

      <DialogActions
        sx={{
          padding: "12px 24px 20px",
          justifyContent: "space-between",
        }}
      >
        <Box>
          {!isFirstStep && (
            <ButtonSecondary
              size={"md"}
              onClick={goBack}
              disabled={isSubmitting}
            >
              <Typography
                fontWeight={600}
                fontSize="0.875rem"
                lineHeight="1rem"
              >
                {t("actions.back")}
              </Typography>
            </ButtonSecondary>
          )}
        </Box>
        <ButtonPrimary size={"md"} onClick={handleNext} disabled={isSubmitting}>
          {isLastStep
            ? isSubmitting
              ? String(t("actions.submitting"))
              : String(t("actions.complete"))
            : String(t("actions.next"))}
        </ButtonPrimary>
      </DialogActions>
    </Dialog>
  );
};

export default SessionWizardModal;
